<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 26/11/2021
  -->

<template>
	<div>
		<div class="relative">

			<div class="mb-2">
				<div class="qw-row-md">
					<div class="qw-row">
						<FormPickerButton @formPicked="onFormPicked"/>
						<span class="mx-2">or</span>
						<qw-button @click="onCreateFormClicked">Create form</qw-button>
					</div>

					<div class="layout-spacer"/>

					<div class="qw-row flex-nowrap mt-2 mt-lg-0">
						<FormSwitchRow
							v-if="eventType.intakeFormId"
							:title="eventType.isIntakeFormEnabled ? 'Intake form is enabled' : 'Intake form is disabled'"
							v-model="eventType.isIntakeFormEnabled"
							@change="updateSessionType"
						/>
						<ToolTipView class="px-2">
							<div v-if="eventType.isIntakeFormEnabled">
								This form will be shown to your clients during booking
							</div>
							<div v-else>
								This form is disable and doesn't accept new responses.
							</div>
						</ToolTipView>
					</div>

				</div>
				<div v-if="eventType.isIntakeFormEnabled && formItem && formItem.isEditable" class="mt-2">
					Note: changes to this form will be reflected everywhere it is used.
				</div>
			</div>

			<div v-if="eventType.isIntakeFormEnabled">
				<FormView
					v-if="formItem && eventType.isIntakeFormEnabled"
					:coachId="ownerProfile.id"
					:show-form-enable-switch="false"
					v-model="formItem"/>

				<div class="text-center mb-5" v-if="eventType.isIntakeFormEnabled && formLoaded && !errorLoadingForm">
					<div>This form will automatically collect Full Name and Email Address before booking this session.</div>
					<div>Click "Add question" to collect additional information from your invitees.</div>
				</div>
			</div>
			<qw-empty v-else-if="!eventType.intakeFormId || !formItem">
				<template #title>
					Add in-take form for this session type.
				</template>
				<template #sub-title>
					To collect additional information from your clients when they book a session with you, create a new
					form or pick an existing one above.
				</template>
				<template #button>
					<FormPickerButton  @formPicked="onFormPicked"/>
				</template>
			</qw-empty>
			<div v-else>

				<qw-empty>
					<template #title>
						Intake form is disabled
					</template>
					<template #sub-title>
						To collect additional information from your clients when they book a session with you, enable the
						intake form above.
					</template>
				</qw-empty>

			</div>


			<LoadingIndicator/>
		</div>
	</div>
</template>

<script>
import BaseComponent from "@/core/components/BaseComponent.vue";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
import EventType from "@shared/scheduler/EventType";
import FormItem from "@/core/modules/coach/Resources/Forms/model/FormItem";
import FormView from "@/core/modules/coach/Resources/Forms/FormView.vue";
import FormPickerButton from "@/core/components/Dialogs/PickerDilaog/FormPickerButton.vue";
import {yesNoDialog} from "@/core/components/Dialogs/dialog.js";
import FormSwitchRow from "@/core/components/Form/FormSwitchRow.vue";
import ToolTipView from "@/core/components/Form/Base/ToolTipView.vue";

export default {
	name: "SessionIntakeFormView",
	components: {ToolTipView, FormSwitchRow, FormPickerButton, FormView, LoadingIndicator},
	extends: BaseComponent,
	props: {
		eventType: {EventType, required: true},
	},

	data() {
		return {
			formLoaded: false,
			formItem: null
		}
	},

	async mounted() {
		await this.handleAsync(this.loadForm())
	},

	methods: {

		async onFormPicked(formItem) {
			console.log("onFormPicked", formItem)
			let res = true

			if(this.eventType.intakeFormId && this.eventType.isIntakeFormEnabled){
				let additionalText =  "<br/><br/>This will replace your existing form."
				res = await yesNoDialog({
					title: "Change form",
					message: `Are you sure you want to use '${formItem.title}' form for this session type?` + additionalText,
					yesButton: "Yes",
					noButton: "Cancel",
				})
			}

			if (res) {
				await this.handleAsync(async () => {
					this.eventType.intakeFormId = formItem.id
					this.eventType.isIntakeFormEnabled = true
					await this.updateSessionType()
					this.formItem = formItem
				})
			}
		},

		async updateSessionType() {
			await this.handleAsyncNonBlocking(this.dataManager.setEventType(this.eventType))
		},

		async onCreateFormClicked() {
			let additionalText = this.eventType.intakeFormId && this.eventType.isIntakeFormEnabled ? "<br/><br/>This will replace your existing form. The existing form will still be available under 'Resources'" : ""
			let res = await yesNoDialog({
				title: "Create a new in-take form",
				message: "Are you sure you want to create a new form for this session type?" + additionalText,
				yesButtonText: "Create",
				noButtonText: "Cancel",
			})
			if (res) {
				await this.handleAsync(this.createForm())
			}
		},

		async createForm() {
			this.formLoaded = false

			let formItem = this.createDefaultIntakeForm()

			this.eventType.intakeFormId = formItem.id

			await Promise.all([
				this.dataManager.setFormItem(formItem),
				this.dataManager.setEventType(this.eventType),
			])

			this.formItem = formItem

			this.formLoaded = true
		},

		async loadForm() {
			if (this.eventType.intakeFormId) {
				this.formItem = await this.$dataManager.getFormItem(this.eventType.intakeFormId)
			}
			if (this.formItem) {
				this.formLoaded = true
			}
		},

		createDefaultIntakeForm() {
			let formTitle = `In-Take Form`
			if(this.eventType.displayName){
				formTitle += ` for ${this.eventType.displayName}`
			}
			let formItem = FormItem.createEmptyFormItem(formTitle, {skipTitle: true, skipExtraQuestion: true})
			if(!this.isOwner){
				formItem.setCreatedBy(this.userProfile)
			}


			let nameElement = formItem.findFullNameElement()
			let emailElement = formItem.findEmailElement()

			nameElement.showDescription = false
			nameElement.showTypeSelect = false
			nameElement.showDelete = false
			nameElement.showCopy = false

			emailElement.showTypeSelect = false
			emailElement.showDescription = false
			emailElement.showDelete = false
			emailElement.showCopy = false


			return formItem
		}

	},

	computed: {
		sessionFullUrlComputed() {
			return this.sessionFullUrl(this.eventType)
		},

		errorLoadingForm() {
			return this.formItem === null && this.formLoaded
		}
	},

}
</script>

<style lang="scss">

</style>
